import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Card,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, ContentCopy, Email, Visibility, FileDownload } from '@mui/icons-material';
import { getInfluencers } from '../services/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

function OnboardingForms({ user }) {
    const [influencers, setInfluencers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [emailRowId, setEmailRowId] = useState(null);
    const [sendingRowId, setSendingRowId] = useState(null);
    const [activeTab, setActiveTab] = useState(
        user?.role === 'onboarder' ? 'assign' : 'direct'
    );
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedInfluencerId, setSelectedInfluencerId] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [users, setUsers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false);
    const [bulkAssignUserId, setBulkAssignUserId] = useState('');
    const [bulkAssigning, setBulkAssigning] = useState(false);
    const [filterUser, setFilterUser] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchInfluencers();
        fetchUsers();
    }, []);

    const fetchInfluencers = async () => {
        setLoading(true);
        try {
            const response = await getInfluencers();
            setInfluencers(response.data);
        } catch (error) {
            console.error('Failed to fetch influencers:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const { default: api } = await import('../services/api');
            const response = await api.get('/users');
            const filteredUsers = response.data.filter(user => user.role !== 'superadmin' && user.role !== 'super_admin');
            setUsers(filteredUsers);
        } catch (error) {
            console.error('Fetch users error:', error);
        }
    };

    const handleAssignClick = (influencerId) => {
        setSelectedInfluencerId(influencerId);
        setAssignModalOpen(true);
    };

    const handleUserAssign = async () => {
        if (!selectedUserId) {
            toast.error('Please select a user');
            return;
        }
        try {
            const { default: api } = await import('../services/api');
            await api.put(`/influencers/assign/${selectedInfluencerId}`, { onboarderId: selectedUserId });
            toast.success('Influencer assigned successfully');
            setAssignModalOpen(false);
            setSelectedUserId('');
            setSelectedInfluencerId('');
            fetchInfluencers();
        } catch (error) {
            toast.error('Failed to assign influencer');
        }
    };

    const handleBulkAssign = async () => {
        if (!bulkAssignUserId) {
            toast.error('Please select a user');
            return;
        }
        setBulkAssigning(true);
        try {
            const { default: api } = await import('../services/api');
            await Promise.all(
                selectedIds.map(id => api.put(`/influencers/assign/${id}`, { onboarderId: bulkAssignUserId }))
            );
            toast.success(`${selectedIds.length} influencers assigned successfully`);
            setBulkAssignModalOpen(false);
            setBulkAssignUserId('');
            setSelectedIds([]);
            fetchInfluencers();
        } catch (error) {
            toast.error('Failed to bulk assign');
        } finally {
            setBulkAssigning(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'success';
            case 'rejected': return 'error';
            default: return 'warning';
        }
    };

    const handleCopyUrl = () => {
        const formUrl = `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/0`;
        navigator.clipboard.writeText(formUrl);
        toast.success('Form URL copied to clipboard!');
    };
    const handleEditCopyUrl = (rowId) => {
        const formUrl = `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/0/${rowId}`;
        navigator.clipboard.writeText(formUrl);
        toast.success('Form URL copied to clipboard!');
    };

    const handleSendEmail = async () => {
        if (!emailAddress) {
            toast.error('Please enter an email address');
            return;
        }
        setEmailSending(true);
        try {
            const { default: api } = await import('../services/api');
            const formUrl = emailRowId
                ? `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/0/${emailRowId}`
                : `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/0`;
            await api.post('/send-form-email', { email: emailAddress, formUrl: formUrl });
            setEmailModalOpen(false);
            setEmailAddress('');
            setEmailRowId(null);
            toast.success('Email sent successfully!');
        } catch (error) {
            toast.error('Failed to send email');
        } finally {
            setEmailSending(false);
        }
    };
    const handleExportExcel = () => {
        const tabLabel = activeTab === 'direct' ? 'Direct' : activeTab === 'assign' ? 'Assign' : 'Complete';
        const dataRows = rows.map((row) => {
            const inf = row.allData;
            const instagramNoOfPost = inf?.instagramNoOfPost || 10;
            const youtubeCount = inf?.youtubeType === 'Shorts'
                ? (Number(inf?.youtubeNoOfShorts) || 10)
                : (Number(inf?.youtubeNoOfPost) || 10);

            const avgViewIG = inf?.last10ReelsViews ? Number((Number(inf.last10ReelsViews) / instagramNoOfPost).toFixed(2)) : '-';
            const avgReachIG = inf?.last10ReelsReach ? Number((Number(inf.last10ReelsReach) / instagramNoOfPost).toFixed(2)) : '-';
            const engIG = (() => {
                const reach = Number(inf?.last10ReelsReach);
                if (!reach) return '-';
                const avgR = reach / instagramNoOfPost;
                if (!avgR) return '-';
                const total = (Number(inf?.instagramLikes) || 0) + (Number(inf?.instagramComments) || 0) +
                    (Number(inf?.instagramSaves) || 0) + (Number(inf?.instagramShares) || 0) + (Number(inf?.instagramReposts) || 0);
                return Number(((total / avgR) * 100).toFixed(2));
            })();

            const ytCost = inf?.youtubeType === 'Shorts' ? (row.youtubeShortsCost || '-') : (row.youtubeVideoCost || '-');
            const avgViewYT = inf?.last10ShortsViews ? Number((Number(inf.last10ShortsViews) / youtubeCount).toFixed(2)) : '-';
            const avgReachYT = inf?.last10ShortsReach ? Number((Number(inf.last10ShortsReach) / youtubeCount).toFixed(2)) : '-';
            const engYT = (() => {
                const reach = Number(inf?.last10ShortsReach);
                if (!reach) return '-';
                const avgR = reach / youtubeCount;
                if (!avgR) return '-';
                const total = (Number(inf?.youtubeLikes) || 0) + (Number(inf?.youtubeComments) || 0) + (Number(inf?.youtubeShares) || 0);
                return Number(((total / avgR) * 100).toFixed(2));
            })();

            const date = inf?.createdAt ? new Date(inf.createdAt) : null;
            const createdAt = date
                ? `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
                : '-';

            return [
                row.sno,
                row.influencerCode || '-',
                row.onboarderName || '-',
                row.name || '-',
                row.email || '-',
                row.reelCost || '-',
                avgViewIG,
                avgReachIG,
                engIG,
                row.youtubeType || '-',
                ytCost,
                avgViewYT,
                avgReachYT,
                engYT,
                createdAt
            ];
        });

        const colHeaders = [
            'SNO', 'Influencer ID', 'Onboard Name', 'Name', 'Email',
            'Total Cost (Instagram)', 'Avg View (Instagram)', 'Avg Reach (Instagram)', 'Engagement Rate (Instagram)',
            'YouTube Type', 'Total Cost (YouTube)', 'Avg View (YouTube)', 'Avg Reach (YouTube)', 'Engagement Rate (YouTube)',
            'Created At'
        ];

        const wsData = [colHeaders, ...dataRows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, tabLabel);
        XLSX.writeFile(wb, `onboarding_${tabLabel.toLowerCase()}.xlsx`);
    };

    const handleEditSendEmailClick = async (rowId, rowEmail) => {
        if (!rowEmail) {
            setEmailRowId(rowId);
            setEmailAddress('');
            setEmailModalOpen(true);
            return;
        }
        setSendingRowId(rowId);
        try {
            const { default: api } = await import('../services/api');
            const formUrl = `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/0/${rowId}`;
            await api.post('/send-form-email', { email: rowEmail, formUrl: formUrl });
            toast.success('Email sent successfully!');
        } catch (error) {
            toast.error('Failed to send email');
        } finally {
            setSendingRowId(null);
        }
    };

    const columns = [
        { field: 'sno', headerName: 'SNO', width: 80 },
        { field: 'influencerCode', headerName: 'Influencer ID', width: 100 },
        { field: 'onboarderName', headerName: 'Onboard Name', width: 150 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'email', headerName: 'Email', width: 200 },
        {
            field: "totalcostinstagram",
            headerName: 'Total Cost Instagram',
            flex: 1,
            minWidth: 200,
            headerClassName: 'instagram-header',
            cellClassName: 'instagram-cell',
            renderCell: (params) => {
                const value = params.row?.reelCost;
                if (!value || isNaN(value)) return "-";
                return `₹${Number(value).toLocaleString()}`;
            }
        },
        {
            field: "AverageViewInstagram",
            headerName: 'Average view (Instagram)',
            flex: 1,
            minWidth: 200,
            headerClassName: 'instagram-header',
            cellClassName: 'instagram-cell',
            renderCell: (params) => {
                const totalViews = params.row?.allData?.last10ReelsViews;

                if (!totalViews || isNaN(totalViews)) {
                    return "-";
                }
                const instagramNoOfPost = params.row?.allData?.instagramNoOfPost || 10;
                const average = Number(totalViews) / instagramNoOfPost;
                return Number(average.toFixed(2));
            }
        },
        {
            field: "AverageReachInstagram",
            headerName: 'Average reach (Instagram)',
            flex: 1,
            minWidth: 200,
            headerClassName: 'instagram-header',
            cellClassName: 'instagram-cell',
            renderCell: (params) => {
                const totalReach = params.row?.allData?.last10ReelsReach;

                if (!totalReach || isNaN(totalReach)) {
                    return "-";
                }
                const instagramNoOfPost = params.row?.allData?.instagramNoOfPost || 10;
                const average = Number(totalReach) / instagramNoOfPost;
                return Number(average.toFixed(2));
            }
        },
        {
            field: "engagementRateInstagram",
            headerName: "Engagement Rate (Instagram)",
            flex: 1,
            minWidth: 200,
            headerClassName: 'instagram-header',
            cellClassName: 'instagram-cell',
            renderCell: (params) => {
                const likes = Number(params.row?.allData?.instagramLikes) || 0;
                const comments = Number(params.row?.allData?.instagramComments) || 0;
                const saves = Number(params.row?.allData?.instagramSaves) || 0;
                const shares = Number(params.row?.allData?.instagramShares) || 0;
                const reposts = Number(params.row?.allData?.instagramReposts) || 0;

                const totalReach = Number(params.row?.allData?.last10ReelsReach);
                if (!totalReach || isNaN(totalReach)) return "-";

                const instagramNoOfPost = params.row?.allData?.instagramNoOfPost || 10;

                const avgReach = totalReach / instagramNoOfPost;
                if (avgReach === 0) return "-";

                const engagementRate =
                    ((likes + comments + saves + shares + reposts) / avgReach) * 100;

                return Number(engagementRate.toFixed(2));
            }
        },
        {
            field: 'youtubeType',
            headerName: 'YouTube Type',
            width: 150,
            headerClassName: 'youtube-header',
            cellClassName: 'youtube-cell',
        },
        {
            field: "totalcostyoutube",
            headerName: "Total Cost Youtube",
            flex: 1,
            minWidth: 200,
            headerClassName: 'youtube-header',
            cellClassName: 'youtube-cell',
            renderCell: (params) => {
                let val;
                if (params.row?.youtubeType == 'Shorts') {
                    val = params.row?.youtubeShortsCost;
                } else {
                    val = params.row?.youtubeVideoCost;
                }
                if (!val) return "-";
                return `₹${val.toLocaleString()}`;
            }
        },
        {
            field: "AverageViewYoutube",
            headerName: 'Average view (YouTube)',
            flex: 1,
            minWidth: 200,
            headerClassName: 'youtube-header',
            cellClassName: 'youtube-cell',
            renderCell: (params) => {
                let totalViews = params.row?.allData?.last10ShortsViews;

                if (!totalViews || isNaN(totalViews)) {
                    return "-";
                }

                let youtubeCount;

                if (params.row?.youtubeType === 'Shorts') {
                    youtubeCount = Number(params.row?.allData?.youtubeNoOfShorts) || 10;
                }
                else if (params.row?.youtubeType === 'Post') {
                    youtubeCount = Number(params.row?.allData?.youtubeNoOfPost) || 10;
                }
                else {
                    return "-";
                }

                const average = Number(totalViews) / youtubeCount;

                return Number(average.toFixed(2));
            }
        },
        {
            field: "AverageReachYoutube",
            headerName: 'Average Reach (YouTube)',
            flex: 1,
            minWidth: 200,
            headerClassName: 'youtube-header',
            cellClassName: 'youtube-cell',
            renderCell: (params) => {
                const totalReach = params.row?.allData?.last10ShortsReach;

                if (!totalReach || isNaN(totalReach)) {
                    return "-";
                }

                let youtubeCount;

                if (params.row?.youtubeType === 'Shorts') {
                    youtubeCount = Number(params.row?.allData?.youtubeNoOfShorts) || 10;
                }
                else if (params.row?.youtubeType === 'Post') {
                    youtubeCount = Number(params.row?.allData?.youtubeNoOfPost) || 10;
                }
                else {
                    return "-";
                }

                const average = Number(totalReach) / youtubeCount;

                return Number(average.toFixed(2));
            }
        },
        {
            field: "engagementRateYoutube",
            headerName: "Engagement Rate (Youtube)",
            flex: 1,
            minWidth: 230,
            headerClassName: 'youtube-header',
            cellClassName: 'youtube-cell',
            renderCell: (params) => {
                const likes = Number(params.row?.allData?.youtubeLikes);
                const comments = Number(params.row?.allData?.youtubeComments);
                const saves = Number(params.row?.allData?.youtubeSaves);
                const shares = Number(params.row?.allData?.youtubeShares);

                const totalReach = Number(params.row?.allData?.last10ShortsReach);
                if (!totalReach || isNaN(totalReach)) return "-";

                let youtubeCount;

                if (params.row?.youtubeType === 'Shorts') {
                    youtubeCount = Number(params.row?.allData?.youtubeNoOfShorts) || 10;
                }
                else if (params.row?.youtubeType === 'Post') {
                    youtubeCount = Number(params.row?.allData?.youtubeNoOfPost) || 10;
                }
                else {
                    return "-";
                }

                const avgReach = totalReach / youtubeCount;

                if (avgReach === 0) return "-";

                const engagementRate =
                    ((likes + comments + shares) / avgReach) * 100;

                return Number(engagementRate.toFixed(2));
            }
        },
        // {
        //   field: 'status',
        //   headerName: 'Status',
        //   width: 100,
        //   renderCell: (params) => (
        //     <Chip
        //       label={params.value}
        //       color={getStatusColor(params.value)}
        //       size="small"
        //       sx={{ textTransform: 'capitalize' }}
        //     />
        //   )
        // },
        {
            field: 'createdAt',
            headerName: 'Created At',
            width: 200,
            type: 'date',
            valueGetter: (params) => {
                if (!params.value) return null;
                const d = new Date(params.value);
                return new Date(d.getFullYear(), d.getMonth(), d.getDate());
            },
            renderCell: (params) => {
                if (!params.value) return '-';
                const date = params.value;
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 250,
            renderCell: (params) => {
                if (activeTab === 'direct') {
                    return (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleAssignClick(params.row.id)}
                            >
                                Assign
                            </Button>
                            <Button
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => navigate(`/influencer/${params.row._id}`)}
                            >
                                View
                            </Button>
                        </Box>
                    );
                }
                else if (activeTab === 'complete') {
                    return (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => navigate(`/influencer/${params.row._id}`)}
                            >
                                View
                            </Button>
                        </Box>
                    );
                } else {
                    return (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                startIcon={<ContentCopy />}
                                onClick={() => handleEditCopyUrl(params.row.id)}
                            >
                                Copy
                            </Button>
                            <Button
                                size="small"
                                startIcon={<Email />}
                                onClick={() => handleEditSendEmailClick(params.row.id, params.row.email)}
                                disabled={sendingRowId === params.row.id}
                            >
                                {sendingRowId === params.row.id ? 'Sending...' : 'Email'}
                            </Button>
                            <Button
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => navigate(`/influencer/${params.row._id}`)}
                            >
                                View
                            </Button>
                        </Box>
                    );
                }

            }
        }

    ];

    const getFilteredInfluencers = () => {
        return influencers.filter(influencer => {
            const onboarderRole = influencer?.onboarderId?.role;
            const panNumber = influencer?.panNumber;

            if (activeTab === 'direct') {
                if (onboarderRole !== 'admin') return false;
            } else if (activeTab === 'assign') {
                if (onboarderRole !== 'onboarder' || panNumber) return false;
            } else if (activeTab === 'complete') {
                if (!panNumber) return false;
            }

            if (filterUser && influencer?.onboarderId?._id !== filterUser) return false;

            if (filterDateFrom) {
                const from = new Date(filterDateFrom);
                from.setHours(0, 0, 0, 0);
                if (new Date(influencer.createdAt) < from) return false;
            }
            if (filterDateTo) {
                const to = new Date(filterDateTo);
                to.setHours(23, 59, 59, 999);
                if (new Date(influencer.createdAt) > to) return false;
            }

            return true;
        });
    };

    const rows = getFilteredInfluencers().map((influencer, index) => ({
        id: influencer._id,
        sno: index + 1,
        influencerCode: influencer.influencerCode,
        name: influencer.name,
        email: influencer.email,
        onboarderName: influencer?.onboarderId?.name || 'N/A',
        platformType: influencer.platformType,
        reelCost: influencer?.reelCost,
        youtubeType: influencer.youtubeType,
        youtubeVideoCost: influencer?.youtubeVideoCost,
        youtubeShortsCost: influencer?.youtubeShortsCost,
        followers: (influencer.instagramFollowers || influencer.youtubeFollowers || 0).toLocaleString(),
        category: influencer.category,
        status: influencer.status,
        createdAt: influencer.createdAt,
        _id: influencer._id,
        allData: influencer
    }));

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
                    Onboarding Forms
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<FileDownload />}
                        onClick={handleExportExcel}
                    >
                        Export Excel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<ContentCopy />}
                        onClick={handleCopyUrl}
                    >
                        Copy Form URL
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Email />}
                        onClick={() => { setEmailRowId(null); setEmailModalOpen(true); }}
                    >
                        Send Email
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, val) => { setActiveTab(val); setSelectedIds([]); }}>
                    {(user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'super_admin') && (
                        <Tab label="Direct" value="direct" />
                    )}
                    <Tab label="Assign" value="assign" />
                    <Tab label="Complete" value="complete" />
                </Tabs>
                {activeTab === 'direct' && selectedIds.length > 0 && (
                    <Button
                        variant="contained"
                        onClick={() => setBulkAssignModalOpen(true)}
                    >
                        Bulk Assign ({selectedIds.length})
                    </Button>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by User</InputLabel>
                    <Select
                        value={filterUser}
                        label="Filter by User"
                        onChange={(e) => setFilterUser(e.target.value)}
                    >
                        <MenuItem value="">All Users</MenuItem>
                        {users.filter(u => u.role === 'onboarder' || u.role === 'admin').map((u) => (
                            <MenuItem key={u._id} value={u._id}>{u.name || u.username}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    type="date"
                    label="From Date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 160 }}
                />
                <TextField
                    size="small"
                    type="date"
                    label="To Date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 160 }}
                />
                {(filterUser || filterDateFrom || filterDateTo) && (
                    <Button size="small" onClick={() => { setFilterUser(''); setFilterDateFrom(''); setFilterDateTo(''); }}>
                        Clear
                    </Button>
                )}
            </Box>

            <Card sx={{ p: 2, boxShadow: 2 }}>
                <DataGrid
                    columns={columns}
                    rows={loading ? [] : rows}
                    loading={loading}
                    pageSize={10}
                    checkboxSelection={activeTab === 'direct'}
                    selectionModel={selectedIds}
                    onSelectionModelChange={(ids) => setSelectedIds(ids)}
                    disableRowSelectionOnClick
                    autoHeight
                    className="table_border"
                    sx={{
                        backgroundColor: 'white',
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#F9FAFB'
                        },
                        '& .MuiDataGrid-iconSeparator': {
                            display: 'none'
                        },
                        '& .MuiDataGrid-row': {
                            backgroundColor: 'white',
                            borderLeft: 'solid 1px #e5e7eb',
                            borderRight: 'solid 1px #e5e7eb'
                        },
                        '& .instagram-cell': {
                            backgroundColor: '#e8f4fcb7 !important'
                        },
                        '& .youtube-cell': {
                            backgroundColor: '#fff5e69a !important'
                        }
                    }}
                />
            </Card>

            {/* Email Modal */}
            <Dialog open={emailModalOpen} onClose={() => setEmailModalOpen(false)} maxWidth="sm" fullWidth >
                <DialogTitle>Send Form URL via Email</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailModalOpen(false)} disabled={emailSending}>Cancel</Button>
                    <Button onClick={handleSendEmail} variant="contained" disabled={emailSending} sx={{ bgcolor: '#019383' }}>
                        {emailSending ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assign Modal */}
            <Dialog open={assignModalOpen} onClose={() => setAssignModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign to User</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Select User</InputLabel>
                        <Select
                            value={selectedUserId}
                            label="Select User"
                            onChange={(e) => setSelectedUserId(e.target.value)}
                        >
                            {users.filter(user => user.role === "onboarder").map((user) => (
                                <MenuItem key={user._id} value={user._id}>
                                    {user.name || user.username} - {user.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleUserAssign} variant="contained">Assign</Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Assign Modal */}
            <Dialog open={bulkAssignModalOpen} onClose={() => setBulkAssignModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Bulk Assign ({selectedIds.length} selected)</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>Select User</InputLabel>
                        <Select
                            value={bulkAssignUserId}
                            label="Select User"
                            onChange={(e) => setBulkAssignUserId(e.target.value)}
                        >
                            {users.filter(u => u.role === 'onboarder').map((u) => (
                                <MenuItem key={u._id} value={u._id}>
                                    {u.name || u.username} - {u.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkAssignModalOpen(false)} disabled={bulkAssigning}>Cancel</Button>
                    <Button onClick={handleBulkAssign} variant="contained" disabled={bulkAssigning}>
                        {bulkAssigning ? 'Assigning...' : 'Assign'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default OnboardingForms;